import { Cause, Effect, Exit, Fiber, Layer, Ref, Schema, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { AcceptedWork } from "../../state/accepted-work.ts";
import { makeSessionOwner } from "../../state/session-owner.ts";
import { clipboardResetMs, kvClipboardFailure, type KVClipboardFailure, type KVClipboardSnapshot } from "./clipboard-model.ts";
import { KVClipboardIo, KVClipboards, type KVClipboardSession } from "./clipboard-service.ts";

const closed = (scope: Scope.Scope) => scope.state._tag === "Closed";
export const KVClipboardsLive = (root: Scope.Scope) => Layer.effect(KVClipboards, Effect.gen(function* () {
  const io = yield* KVClipboardIo, accepted = yield* AcceptedWork, registry = yield* AtomRegistry.AtomRegistry;
  const sessions = yield* makeSessionOwner(root);
  const failures = yield* Ref.make<readonly Cause.Cause<KVClipboardFailure>[]>([]);
  const makeSession = (text: string) => Effect.gen(function* () {
    const owner = yield* Scope.Scope;
    const state = yield* Ref.make<{ readonly sequence: number; readonly snapshot: KVClipboardSnapshot }>({ sequence: 0, snapshot: { text, copied: false, refused: false } });
    const timer = yield* Ref.make<Fiber.Fiber<void> | null>(null);
    const read = Ref.get(state).pipe(Effect.map(value => value.snapshot));
    const snapshot = Atom.make(read);
    const alive = () => !closed(root) && !closed(owner);
    const publish = Effect.sync(() => { if (alive()) registry.refresh(snapshot); });
    const stopTimer = Ref.getAndSet(timer, null).pipe(Effect.flatMap(value => value ? Fiber.interrupt(value) : Effect.void));
    const copy = accepted.join(Effect.gen(function* () {
      if (!alive()) return;
      const current = yield* Ref.getAndUpdate(state, value => ({ sequence: value.sequence + 1, snapshot: { text, copied: false, refused: false } }));
      const sequence = current.sequence + 1;
      yield* stopTimer;
      yield* publish;
      const result = yield* Effect.exit(io.writeText(text).pipe(Effect.flatMap(value => Schema.decodeUnknownEffect(Schema.Undefined)(value).pipe(Effect.mapError(kvClipboardFailure)))));
      if (Exit.isFailure(result)) yield* Ref.update(failures, values => [...values, result.cause]);
      const changed = yield* Ref.modify(state, value => alive() && value.sequence === sequence
        ? [true, { ...value, snapshot: { text, copied: Exit.isSuccess(result), refused: Exit.isFailure(result) } }] as const
        : [false, value] as const);
      if (!changed) return;
      yield* publish;
      if (Exit.isFailure(result)) return;
      const reset = Effect.sleep(clipboardResetMs).pipe(Effect.andThen(Ref.update(state, value => alive() && value.sequence === sequence
        ? { ...value, snapshot: { ...value.snapshot, copied: false } } : value)), Effect.andThen(publish));
      const pending = yield* Effect.forkIn(reset, owner, { uninterruptible: false });
      yield* Ref.set(timer, pending);
    }));
    return { snapshot, read, copy } satisfies KVClipboardSession;
  });
  return KVClipboards.of({ open: text => sessions.open(makeSession(text)), failures: Ref.get(failures) });
}));
